/**
 * "No signal" in words a store user understands (round 2, 2026-10-21): a
 * request that never reached the server rejects with the browser's own
 * TypeError ("Failed to fetch" / "NetworkError when attempting to fetch
 * resource." / "Load failed"), which the pages used to print as-is.
 * apiRequest and graphFetch turn it into an OfflineError carrying one of
 * these messages, so every page that shows `err.message` says it plainly.
 */

// "Wasn't confirmed", not "nothing was sent": the connection can drop after the server
// saved it. Trying again is safe — creates carry a ClientKey (security review 2026-10-22).
export const OFFLINE_WRITE_MESSAGE = "You’re offline — it wasn’t confirmed as sent. Try again when you have signal.";
export const OFFLINE_READ_MESSAGE = "You’re offline — couldn’t reach the server. Try again when you have signal.";
/** For screens that still show what is saved on the device (the Audits hub). */
export const OFFLINE_LOCAL_MESSAGE = "You’re offline — showing what’s saved on this device.";

export class OfflineError extends Error {
  constructor(readonly write: boolean) {
    super(write ? OFFLINE_WRITE_MESSAGE : OFFLINE_READ_MESSAGE);
    this.name = 'OfflineError';
  }
}

const browserOffline = () => typeof navigator !== 'undefined' && navigator.onLine === false;

/**
 * True when `err` means the request never got an answer: fetch's TypeError
 * (it rejects with nothing else for network failures), or any failure while
 * the browser says it is offline (e.g. the token refresh couldn't reach
 * Entra). An aborted request is not "offline".
 */
export function isNetworkFailure(err: unknown, offline: boolean = browserOffline()): boolean {
  if (err instanceof OfflineError) return true;
  if (err instanceof DOMException && err.name === 'AbortError') return false;
  return err instanceof TypeError || offline;
}

/** Runs one network step (token + fetch); a network failure becomes an OfflineError. */
export async function offlineAware<T>(step: () => Promise<T>, write: boolean): Promise<T> {
  try {
    return await step();
  } catch (err) {
    if (err instanceof OfflineError) throw err;
    if (isNetworkFailure(err)) throw new OfflineError(write);
    throw err;
  }
}
